import { useId, type ReactNode } from 'react';
import { palette } from '../tokens.ts';

/*
 * Built-in avatars: pennants, burgees and flags of the International Code of Signals,
 * drawn as hand-tinted engravings. Ids must match AVATARS in @brig/api-types.
 */

const RED = '#A23B2C';
const BLUE = '#34506B';
const WHITE = '#F7F0DC';
const BLACK = palette.ink;
const YELLOW = palette.fleetYellow;

// Flag field on a 48×40 canvas, hoisted on the left.
const L = 6;
const R = 44;
const T = 6;
const B = 34;
const MX = (L + R) / 2;
const MY = (T + B) / 2;

const RECT = `M${L} ${T} H${R} V${B} H${L} Z`;
const SWALLOWTAIL = `M${L} ${T} H${R} L${R - 12} ${MY} L${R} ${B} H${L} Z`;
const PENNANT = `M${L} ${T} L${R} ${MY} L${L} ${B} Z`;

interface FlagArt {
  outline: string;
  body: ReactNode;
}

const solid = (outline: string, fill: string): FlagArt => ({
  outline,
  body: <path d={outline} fill={fill} />,
});

const ART: Record<string, FlagArt> = {
  'pennant-yellow': solid(PENNANT, YELLOW),
  'pennant-green': solid(PENNANT, palette.fleetGreen),
  'pennant-crimson': solid(PENNANT, palette.sealRed),
  'pennant-slate': solid(PENNANT, palette.fadedBlue),
  'burgee-gold': solid(SWALLOWTAIL, palette.gold),
  'burgee-blue': solid(SWALLOWTAIL, BLUE),
  'signal-alpha': {
    outline: SWALLOWTAIL,
    body: (
      <>
        <path d={`M${L} ${T} H${MX - 4} V${B} H${L} Z`} fill={WHITE} />
        <path d={`M${MX - 4} ${T} H${R} L${R - 12} ${MY} L${R} ${B} H${MX - 4} Z`} fill={BLUE} />
      </>
    ),
  },
  'signal-bravo': solid(SWALLOWTAIL, RED),
  'signal-delta': {
    outline: RECT,
    body: (
      <>
        <path d={RECT} fill={YELLOW} />
        <rect x={L} y={T + 7} width={R - L} height={14} fill={BLUE} />
      </>
    ),
  },
  'signal-kilo': {
    outline: RECT,
    body: (
      <>
        <rect x={L} y={T} width={MX - L} height={B - T} fill={YELLOW} />
        <rect x={MX} y={T} width={R - MX} height={B - T} fill={BLUE} />
      </>
    ),
  },
  'signal-november': {
    outline: RECT,
    body: (
      <>
        {Array.from({ length: 16 }, (_, i) => {
          const col = i % 4;
          const row = Math.floor(i / 4);
          const w = (R - L) / 4;
          const h = (B - T) / 4;
          return (
            <rect
              key={i}
              x={L + col * w}
              y={T + row * h}
              width={w}
              height={h}
              fill={(col + row) % 2 ? WHITE : BLUE}
            />
          );
        })}
      </>
    ),
  },
  'signal-papa': {
    outline: RECT,
    body: (
      <>
        <path d={RECT} fill={BLUE} />
        <rect x={L + 11} y={T + 8} width={16} height={12} fill={WHITE} />
      </>
    ),
  },
  'signal-uniform': {
    outline: RECT,
    body: (
      <>
        <rect x={L} y={T} width={MX - L} height={MY - T} fill={RED} />
        <rect x={MX} y={T} width={R - MX} height={MY - T} fill={WHITE} />
        <rect x={L} y={MY} width={MX - L} height={B - MY} fill={WHITE} />
        <rect x={MX} y={MY} width={R - MX} height={B - MY} fill={RED} />
      </>
    ),
  },
  'signal-zulu': {
    outline: RECT,
    body: (
      <>
        <path d={`M${L} ${T} H${R} L${MX} ${MY} Z`} fill={YELLOW} />
        <path d={`M${R} ${T} V${B} L${MX} ${MY} Z`} fill={BLUE} />
        <path d={`M${L} ${B} H${R} L${MX} ${MY} Z`} fill={RED} />
        <path d={`M${L} ${T} V${B} L${MX} ${MY} Z`} fill={BLACK} />
      </>
    ),
  },
};

export interface FlagAvatarProps {
  /** Avatar id from @brig/api-types AVATARS. Unknown ids fall back to the yellow pennant. */
  id: string;
  size?: number;
  /** Accessible name; omit for decorative use next to visible text. */
  label?: string;
  className?: string;
}

export function FlagAvatar({ id, size = 48, label, className }: FlagAvatarProps) {
  const uid = useId().replace(/:/g, '');
  const art = ART[id] ?? ART['pennant-yellow'];
  if (!art) return null;
  const hatch = `${uid}-hatch`;
  const clip = `${uid}-clip`;
  return (
    <svg
      className={className}
      viewBox="0 0 48 40"
      width={size}
      height={(size * 40) / 48}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      focusable="false"
    >
      <defs>
        <pattern
          id={hatch}
          width="2.5"
          height="2.5"
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(-30)"
        >
          <line x1="0" y1="1.25" x2="2.5" y2="1.25" stroke={BLACK} strokeWidth="0.35" />
        </pattern>
        <clipPath id={clip}>
          <path d={art.outline} />
        </clipPath>
      </defs>
      {/* Halyard and hoist */}
      <path d={`M${L - 2} 2 V38`} stroke={BLACK} strokeWidth={1.2} strokeLinecap="round" />
      <g clipPath={`url(#${clip})`}>
        {art.body}
        {/* Engraved shading over the tint, heavier toward the fly to suggest a fold */}
        <rect x={MX} y={0} width={R} height={40} fill={`url(#${hatch})`} opacity={0.35} />
      </g>
      <path d={art.outline} fill="none" stroke={BLACK} strokeWidth={1} strokeLinejoin="round" />
    </svg>
  );
}
